import React from 'react';
import { Box, Chip, FilterChip, Modal } from '@drone-js/core';
import { SortItem } from 'component/common/SortItem/SortItem';
import { getReviewSortItems, getSelectedFiltersType } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.utils';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, SortFilterProps } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesReviewSortChipClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesReviewSortChipClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import styled from 'styled-components';
import { body } from 'core-framework/window/document';
import Portal from 'component/common/Portal/Portal';

const ReviewSortModal = styled(Modal)`
    max-width: 300px;
`;

// eslint-disable-next-line @agoda/agoda-a11y/missing-keyboard-tests -- @agoda/agoda-a11y/missing-keyboard-tests[a4297af3-6b28-4b49-822b-177183485312]
const ReviewSortChip: React.FC<SortFilterProps> = (props) => {
    const items = React.useMemo(() => getReviewSortItems(props.cms, props.sortMatrix, props.currentSort), [props.cms, props.currentSort, props.sortMatrix]);
    const [popoverState, setPopoverState] = React.useState(false);
    const [modalState, setModalState] = React.useState({ visible: false });
    const { onSortChangeCreator, activityId, currentFilter } = props;
    const onSortChange = React.useMemo(() => onSortChangeCreator(activityId, currentFilter), [activityId, onSortChangeCreator, currentFilter]);

    if (!items.length) {
        return null;
    }

    const setPopoverVisible = () => {
        setPopoverState(!popoverState);
    };

    const onPopoverBlur = () => {
        setPopoverState(false);
    };

    const setModalVisible = (visible: boolean) => {
        setModalState({ visible });
    };

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedItem = items.find((item) => item.type === e.target.value);
        if (selectedItem) {
            onSortChange({ code: selectedItem.code, order: selectedItem.order, type: selectedItem.type });
            const selectedFiltersText = getSelectedFiltersType(props.currentFilter).join(',');
            trackElementEvent<ActivitiesReviewSortChipClickEventActivitiesFieldsV1>(
                ActivitiesReviewSortChipClickEventActivitiesManifestV1.ids,
                {},
                { action_element_value: `${selectedItem.type},${selectedFiltersText}` },
            );
        }
        setModalState({ visible: false });
    };

    const currentItemLabel = items.find((item) => item.type === props.currentSort.type)?.label;
    const currentSortLabel = currentItemLabel || props.cms[289338];
    const currentSortAriaLabel = `${props.cms[74523]}: ${currentSortLabel}`;

    return props.screenSize.tabletOrDesktop ? (
        <FilterChip
            data-element-name="activities-review-sort-chip"
            data-testid="activities-review-sort-chip"
            label={currentSortLabel}
            onClick={setPopoverVisible}
            onBlur={onPopoverBlur}
            active={popoverState}
            mb="12"
            leadingIcon="fill.arrow.thin-updown"
            aria-label={currentSortAriaLabel}
        >
            <Box role="radiogroup">
                {items.map((item, key) => (
                    <SortItem key={key} item={item} onChangeHandler={onChangeHandler} isLastItem={key === items.length - 1} index={key} />
                ))}
            </Box>
        </FilterChip>
    ) : (
        <>
            <Chip
                data-element-name="activities-review-sort-chip"
                data-testid="activities-review-sort-chip"
                label={currentSortLabel}
                onClick={() => setModalVisible(true)}
                leadingIcon="line.arrow.thin-dropdown"
                aria-label={currentSortAriaLabel}
            />
            <Portal container={body}>
                <ReviewSortModal
                    data-testid="activities-review-sort-modal"
                    title={props.cms[74523]}
                    size="m"
                    visible={modalState.visible}
                    onClose={() => setModalVisible(false)}
                    // z-index sheet 1200 -> sort modal 1201 -> gallery modal 1202
                    zIndex={1201}
                >
                    <Box role="radiogroup">
                        {items.map((item, key) => (
                            <SortItem key={key} item={item} onChangeHandler={onChangeHandler} isLastItem={key === items.length - 1} index={key} />
                        ))}
                    </Box>
                </ReviewSortModal>
            </Portal>
        </>
    );
};
ReviewSortChip.displayName = 'ReviewSortChip';

export default connect(mapStateToProps, mapDispatchToProps)(ReviewSortChip);
